import { useState, useEffect, useRef } from 'react'
import { getURLMetadata } from 'Services/apiAdapter_CoffeeWeb'
import { urlMethods } from 'Utils/commonMethods'
// Custom hook to fetch URL metadata for preview

export const useUrlMetadata = (url, enabled = true) => {
  const [metadata, setMetadata] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortControllerRef = useRef(null)
  const fetchedUrlRef = useRef(null) // Track which URL we've successfully fetched
  const fetchingUrlRef = useRef(null) // Track which URL is currently being fetched
  const isFetchingRef = useRef(false) // Track if we're currently fetching

  // Cleanup function
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    },
    []
  )

  useEffect(() => {
    // Reset state when URL changes to a different URL
    if (fetchedUrlRef.current && fetchedUrlRef.current !== url) {
      setMetadata(null)
      setError(null)
      // Don't reset isFetchingRef here - let the cleanup handle it
    }

    // Reset fetching ref if URL changed
    if (fetchingUrlRef.current && fetchingUrlRef.current !== url) {
      isFetchingRef.current = false
      fetchingUrlRef.current = null
    }

    if (!url) {
      setIsLoading(false)
      setMetadata(null)
      fetchedUrlRef.current = null
      isFetchingRef.current = false

      return
    }

    // If we already have metadata for this exact URL, don't fetch again
    if (fetchedUrlRef.current === url && metadata) {
      setIsLoading(false)

      return
    }

    // If disabled, don't start a new request
    if (!enabled) {
      setIsLoading(false)

      return
    }

    // Don't start a new request if we're already fetching the same URL
    // But allow if URL changed
    if (isFetchingRef.current && fetchingUrlRef.current === url) {
      // Already fetching this exact URL, wait for it to complete
      return
    }

    // Validate URL format - must be a complete, valid URL BEFORE setting loading state

    if (!urlMethods.isValidUrl(url)) {
      setIsLoading(false)
      setMetadata(null)

      return // Don't set error for incomplete URLs, just don't fetch
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    const abortController = new AbortController()

    abortControllerRef.current = abortController

    // Set loading state after validation passes (with a small delay to avoid flashing)
    const loadingTimeoutId = setTimeout(() => {
      setIsLoading(true)
    }, 200) // Show skeleton after 200ms of valid URL

    setError(null)

    // Debounce the API call
    const timeoutId = setTimeout(async () => {
      // Capture URL at the time of execution
      const urlToFetch = url

      // Double-check URL is still valid and we don't already have metadata
      if (!urlToFetch) {
        setIsLoading(false)
        isFetchingRef.current = false
        fetchingUrlRef.current = null

        return
      }

      // If we already have metadata for this URL, skip
      if (fetchedUrlRef.current === urlToFetch && metadata) {
        setIsLoading(false)
        isFetchingRef.current = false
        fetchingUrlRef.current = null

        return
      }

      // Set fetching flag only when we actually start the API call
      isFetchingRef.current = true
      fetchingUrlRef.current = urlToFetch

      try {
        console.log('Making API call for URL:', urlToFetch)
        // The API service returns a promise - await it directly
        // Pass null for generatedToken (optional) and no callback
        const response = await getURLMetadata(urlToFetch, null)

        console.log('API Promise resolved:', response)

        // The response object has a data property containing the actual response
        const finalResponse = response?.data || response

        console.log('Final response data:', finalResponse)

        // Check if request was aborted
        if (abortController.signal.aborted) {
          console.log('Request was aborted')
          isFetchingRef.current = false
          fetchingUrlRef.current = null

          return
        }

        // Verify URL still matches (user might have changed it)
        // Use urlToFetch (captured at timeout start) instead of url (might have changed)
        if (fetchingUrlRef.current !== urlToFetch) {
          console.log('URL changed during fetch, ignoring response. Expected:', urlToFetch, 'Current:', fetchingUrlRef.current)
          isFetchingRef.current = false
          fetchingUrlRef.current = null

          return
        }

        // Parse response - handle both callback and promise response
        // The callback receives response.data directly, promise returns full response object
        console.log('URL Metadata API Response:', finalResponse)

        // Use finalResponse which could be from callback (data) or promise (response.data)
        const data = finalResponse

        // Check if response is a valid object with at least one metadata field
        // if (data && typeof data === 'object' && !Array.isArray(data) && (data.hasOwnProperty('title') || data.hasOwnProperty('description') || data.hasOwnProperty('image'))) {
        if (data && typeof data === 'object' && !Array.isArray(data) && (Object.hasOwn(data, 'title') || Object.hasOwn(data, 'description') || Object.hasOwn(data, 'image'))) {
          const domain = urlMethods.extractDomainFromUrl(urlToFetch)

          // If title is the URL itself, use domain as title, otherwise use the title
          const title = data.title && data.title !== urlToFetch ? data.title : domain

          // Create metadata even if some fields are missing
          const parsedMetadata = {
            title: title || domain,
            description: data.description || '',
            image: data.image || '',
            url: urlToFetch,
            siteName: data.siteName || domain
          }

          console.log('Parsed metadata:', parsedMetadata)
          // Set metadata if we're still fetching this URL (URL hasn't changed)
          if (fetchingUrlRef.current === urlToFetch) {
            console.log('Setting metadata state for URL:', urlToFetch)
            setMetadata(parsedMetadata)
            setError(null)
            fetchedUrlRef.current = urlToFetch // Mark this URL as successfully fetched
          } else {
            console.log('URL changed - not setting metadata. Expected:', urlToFetch, 'Current fetching:', fetchingUrlRef.current)
          }
        } else {
          console.warn('Invalid URL metadata response structure:', response)
          setError('Invalid response format')
          setMetadata(null)
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (!abortController.signal.aborted) {
          console.error('Error fetching URL metadata:', err)
          setError(err.message || 'Failed to fetch URL metadata')
          setMetadata(null)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
          isFetchingRef.current = false
          // Only clear fetchingUrlRef if this was the URL we were fetching
          if (fetchingUrlRef.current === urlToFetch) {
            fetchingUrlRef.current = null
          }
        }
      }
    }, 500) // 500ms debounce

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(loadingTimeoutId)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      // Don't reset isFetchingRef here - let it be reset in the finally block
      // Only reset loading if we're cleaning up before the timeout executes
      setIsLoading(false)
    }
  }, [url, enabled]) // Don't include metadata to avoid infinite loops

  return { metadata, isLoading, error }
}
